import { z } from "zod";
import { dateOnlySchema, nullableText, versionSchema } from "./common.js";

export const CALENDAR_VIEWS = ["day", "week", "month"] as const;
export type CalendarView = (typeof CALENDAR_VIEWS)[number];

export const CALENDAR_EVENT_KINDS = ["holiday", "project"] as const;
export type CalendarEventKind = (typeof CALENDAR_EVENT_KINDS)[number];

export const calendarRangeSchema = z
  .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    view: z.enum(CALENDAR_VIEWS).default("week"),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.to < value.from) {
      ctx.addIssue({
        code: "custom",
        path: ["to"],
        message: "Calendar end must not precede start",
      });
    }
  });
export type CalendarRangeDto = z.infer<typeof calendarRangeSchema>;
export const calendarOverviewQuerySchema = calendarRangeSchema;
export type CalendarOverviewQueryDto = CalendarRangeDto;

export const calendarEventSchema = z
  .object({
    id: z.string().min(1).max(160),
    uid: z.string().min(1).max(320),
    kind: z.enum(CALENDAR_EVENT_KINDS),
    date: dateOnlySchema,
    start: z.string().datetime().nullable().optional(),
    end: z.string().datetime().nullable().optional(),
    title: z.string().trim().min(1).max(240),
    description: nullableText(2000),
    holidayName: nullableText(240),
    projectId: z.string().uuid().nullable().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.start && value.end && value.end <= value.start) {
      ctx.addIssue({
        code: "custom",
        path: ["end"],
        message: "Calendar event end must be after start",
      });
    }
  });
export type CalendarEventDto = z.infer<typeof calendarEventSchema>;

export const calendarOverviewResponseSchema = z
  .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    events: z.array(calendarEventSchema),
  })
  .strict();
export type CalendarOverviewResponseDto = z.infer<
  typeof calendarOverviewResponseSchema
>;

export const calendarFeedTokenCreateSchema = z.object({}).strict();
export type CalendarFeedTokenCreateDto = z.infer<
  typeof calendarFeedTokenCreateSchema
>;

export const calendarFeedTokenRotateSchema = z
  .object({ version: versionSchema })
  .strict();
export type CalendarFeedTokenRotateDto = z.infer<
  typeof calendarFeedTokenRotateSchema
>;

export const calendarFeedTokenRevokeSchema = calendarFeedTokenRotateSchema;
export type CalendarFeedTokenRevokeDto = CalendarFeedTokenRotateDto;

export const calendarFeedTokenParamSchema = z
  .object({ token: z.string().trim().min(1).max(512) })
  .strict();
export type CalendarFeedTokenParamDto = z.infer<
  typeof calendarFeedTokenParamSchema
>;

export const calendarFeedResponseSchema = z
  .object({
    token: z.string().trim().min(1).max(512),
    url: z.string().url().max(2048),
    createdAt: z.string().datetime(),
    lastAccessAt: z.string().datetime().nullable(),
  })
  .strict();
export type CalendarFeedResponseDto = z.infer<
  typeof calendarFeedResponseSchema
>;
